import {
  addQuarters,
  addYears,
  endOfQuarter,
  endOfYear,
  formatDistanceToNowStrict,
  isSameDay,
  isValid,
  parse,
  subQuarters,
  subYears,
} from 'date-fns';
import { Temporal } from 'temporal-polyfill';
import { RendererType } from '../data-models/field-definition';
import { captureUnexpectedState } from './SentryUtils';

/** Today's date in UTC as `YYYY-MM-DD`, the day the API serves for. */
export function todayUtc(): string {
  return Temporal.Now.plainDateISO('UTC').toString();
}

export function extractISODate(dateStr: string): string {
  return dateStr.split('T')[0];
}

export function dateToPlainDateString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/** Whether an ISO date's calendar day is after today's local day; non-ISO input is never future. */
export function isFutureDated(isoDate: string | null | undefined, today = new Date()): boolean {
  return ISO_DATE_PREFIX.test(isoDate ?? '') && String(isoDate).slice(0, 10) > dateToPlainDateString(today);
}

/** The calendar day after `isoDate` (`YYYY-MM-DD`), e.g. for an exclusive upper bound. */
export function nextPlainDateString(isoDate: string): string {
  return Temporal.PlainDate.from(isoDate).add({ days: 1 }).toString();
}

export function plainDateToZonedDateTime(isoDate: string): Date {
  const [year, month, day] = extractISODate(isoDate).split('-');

  return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A `YYYY-MM-DD` string as local midnight (`new Date()` reads it as UTC, a day early west of UTC); anything else as-is. */
export function dateFromDateString(value: string): Date {
  return DATE_ONLY.test(value) ? plainDateToZonedDateTime(value) : new Date(value);
}

export function plainDateFromIsoDateTimeString(dateStr: string): Temporal.PlainDate {
  return Temporal.PlainDate.from(extractISODate(dateStr));
}

export function dateFromTimelessString(dateStr: string) {
  const temporalDateStr = Temporal.PlainDateTime.from(dateStr).toString();
  return new Date(temporalDateStr);
}

export function TemporalPlainDateFromJsDate(date: Date): Temporal.PlainDate {
  return Temporal.PlainDate.from({
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear(),
  });
}

export function getEarlierTemporalDate(
  date1: Temporal.PlainDate,
  date2: Temporal.PlainDate
): Temporal.PlainDate {
  return date1.until(date2).days < 0 ? date2 : date1;
}

/**
 * The calendar date a stored string represents, as `YYYY-MM-DD`, or null when it isn't a
 * single date. Strict on purpose: values like `"10/29/2018,12/03/2021"`, `"2018"`, or
 * `"2/30/2025"` — which lenient `Date.parse`-based utils coerce or blank — return null so
 * callers can fall back to the raw value.
 */
export function toSingleDateString(value: string): string | null {
  const trimmed = value.trim();
  try {
    return Temporal.PlainDate.from(trimmed).toString();
  } catch {
    // PlainDate.from rejects `Z`-suffixed timestamps; fall through to the checks below.
  }
  try {
    // Instant.from validates the whole string as one timestamp — extracting the date
    // part first would accept any junk after a valid prefix (e.g. "…Z,…Z" rollups).
    Temporal.Instant.from(trimmed);
    return plainDateFromIsoDateTimeString(trimmed).toString();
  } catch {
    const usDate = parse(trimmed, 'M/d/yyyy', new Date());
    // The year floor keeps 2-digit years (e.g. "05/15/25") verbatim instead of year 0025.
    return isValid(usDate) && usDate.getFullYear() >= 1000 ? dateToPlainDateString(usDate) : null;
  }
}

export function plainDateFromDateOrDateString(value: string | Date): string {
  if (value instanceof Date) {
    return dateToPlainDateString(value);
  }
  if (typeof value === 'string' && isDateString(value, RendererType.date)) {
    return dateToPlainDateString(plainDateToZonedDateTime(new Date(value).toISOString()));
  }
  captureUnexpectedState('plainDateFromDateOrDateString: invalid date value', { value });
  return value;
}

export function isDateOrDateString(value: unknown, type: RendererType) {
  return isDateString(value, type) || (value instanceof Date && !isNaN(value.getTime()));
}

// Reduce rather than Math.min(...spread) — the chart callers pass one entry per
// transaction, and a large enough spread blows the call stack.
export function earliestOf(dates: Date[]): Date | null {
  return dates.reduce<Date | null>((min, date) => (min === null || date < min ? date : min), null);
}

export function isInvalidDate(value: unknown) {
  return value instanceof Date && isNaN(value.getTime());
}

export function isValidDate(value: unknown) {
  return value instanceof Date && !isNaN(value.getTime());
}

export function isDateString(value: unknown | null | undefined, type: RendererType) {
  return (
    type === RendererType.date && value != null && typeof value === 'string' && !isNaN(Date.parse(value))
  );
}

/** Human-readable relative time, e.g. "3 hours ago". Returns '—' for a null/invalid date. */
export function relativeTimeFromNow(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) {
    return '—';
  }
  return `${formatDistanceToNowStrict(d)} ago`;
}

/** The last calendar quarter end strictly before the date's day, as an end-of-day instant. */
export function previousQuarterEnd(date: Date): Date {
  return endOfQuarter(subQuarters(date, 1));
}

/** The first calendar quarter end strictly after the date's day, as an end-of-day instant. */
export function nextQuarterEnd(date: Date): Date {
  const quarterEnd = endOfQuarter(date);
  return isSameDay(quarterEnd, date) ? endOfQuarter(addQuarters(date, 1)) : quarterEnd;
}

/** The last 31 December strictly before the date's day, as an end-of-day instant. */
export function previousYearEnd(date: Date): Date {
  return endOfYear(subYears(date, 1));
}

/** The first 31 December strictly after the date's day, as an end-of-day instant. */
export function nextYearEnd(date: Date): Date {
  const yearEnd = endOfYear(date);
  return isSameDay(yearEnd, date) ? endOfYear(addYears(date, 1)) : yearEnd;
}
